/**
 * Guarda el ID token de Google fuera de React.
 *
 * El cliente HTTP se construye una sola vez, antes de que exista el arbol de
 * componentes, y necesita leer el token vigente en cada request. Un modulo con
 * suscripcion resuelve eso sin volver a crear el cliente en cada render.
 *
 * Se usa `sessionStorage` a proposito: el token dura lo que dura la pestana. No
 * queremos un ID token de Google sobreviviendo en `localStorage`.
 */

const STORAGE_KEY = 'amoble.monitor.idToken';

type Listener = () => void;

const listeners = new Set<Listener>();

function readFromStorage(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Navegador con almacenamiento bloqueado: se sigue en memoria.
    return null;
  }
}

let currentToken: string | null = typeof window === 'undefined' ? null : readFromStorage();

export function getToken(): string | null {
  return currentToken;
}

export function setToken(token: string | null): void {
  currentToken = token;
  try {
    if (token === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, token);
    }
  } catch {
    // Sin almacenamiento persistente el token vive solo en memoria; no es un error fatal.
  }
  for (const listener of listeners) {
    listener();
  }
}

export function clearToken(): void {
  setToken(null);
}

export function subscribeToToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
